import { useUser } from "@/hooks/auth/use-user";
import { supabase } from "@/lib/supabase";
import { PasswordInput } from "@/routes/auth/components/password-input";
import { invariant } from "@/utils/invariant";
import { useToastMessage } from "@backpac/monica";
import { Button, Form, FormField, FormItem, FormLabel } from "@backpac/monica";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface ChangePasswordFormProps {
  onCancel: () => void;
  onSubmitted: () => void;
}

const ChangePasswordFormSchema = z
  .object({
    currentPassword: z.string().min(1),
    newPassword: z.string().min(8),
    confirmPassword: z.string().min(8),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

export function ChangePasswordForm({ onCancel, onSubmitted }: ChangePasswordFormProps) {
  const { info, error } = useToastMessage();
  const { data: user } = useUser();
  const form = useForm<z.infer<typeof ChangePasswordFormSchema>>({
    resolver: zodResolver(ChangePasswordFormSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });
  const currentEmail = user?.email;

  invariant(currentEmail != null, "User not found");

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-4"
        id="email-verify"
        onSubmit={form.handleSubmit(async (data) => {
          try {
            const { error: signInError } = await supabase.auth.signInWithPassword({
              email: currentEmail,
              password: data.currentPassword,
            });

            if (signInError) {
              error("Incorrect current password");
              return;
            }

            const response = await supabase.auth.updateUser({
              password: data.newPassword,
            });

            if (response.error) {
              error("Failed to change password");
              return;
            }

            info("Password changed");
            onSubmitted();
          } catch (error) {
            console.error(error);
          }
        })}
      >
        <FormField
          control={form.control}
          name="currentPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Current password</FormLabel>
              <PasswordInput placeholder="Your current password" {...field} />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="newPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>New password</FormLabel>
              <PasswordInput placeholder="Your new password" {...field} />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="confirmPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Confirm password</FormLabel>
              <PasswordInput placeholder="Confirm your new password" {...field} />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2">
          <Button variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
          <Button disabled={form.formState.isSubmitting || !form.formState.isValid} type="submit">
            Change password
          </Button>
        </div>
      </form>
    </Form>
  );
}
